import httpClient from "@/http/httpClient";
import store from "@/store";
import { toast } from "@starline-security/component-library";
import { queryClient } from "@/queryClient";
import { USERS_QUERY_KEY } from "@/hooks/userManagement/useGetUsersInfinite";

const refetchUsers = () => queryClient.invalidateQueries(USERS_QUERY_KEY);

export default {
  namespaced: true,
  state: {
    systemRoles: [],
  },
  mutations: {
    setSystemRoles(state, payload) {
      state.systemRoles = payload;
    },
  },
  actions: {
    async fetchSystemRoles({ commit }) {
      store.commit("preloader/preloader", true);

      try {
        const response = await httpClient.get("settings/user-management/roles");
        commit("setSystemRoles", response.data.data);
      } catch (error) {
        store.commit("errorsModal/openModal", error.response.data.errors);
      } finally {
        store.commit("preloader/preloader", false);
      }
    },
    async removeUser({}, user) {
      store.commit("preloader/preloader", true);
      try {
        const response = await httpClient.post(
          `settings/user-management/users/${user.id}/remove`
        );
        refetchUsers();
        let message =
          user.state === "invitation"
            ? "User invite deleted"
            : user.state === "blocked"
            ? "User unblocked."
            : "User blocked.";

        toast.success(`${response?.data?.message ?? message}`);
      } catch (error) {
        toast.error(`Your changes couldn’t be saved`);
      } finally {
        store.commit("preloader/preloader", false);
      }
    },
    async unlockUser({}, user) {
      store.commit("preloader/preloader", true);
      try {
        const response = await httpClient.post(
          `settings/user-management/users/${user.id}/unlock`
        );
        refetchUsers();
        toast.success(`${response?.data?.message ?? "User unlocked"}`);
      } catch (error) {
        toast.error(`Your changes couldn’t be saved`);
      } finally {
        store.commit("preloader/preloader", false);
      }
    },
    async updateUser({}, userData) {
      let isSuccess = false;
      store.commit("preloader/preloader", true);

      try {
        const data = {
          first_name: userData.firstName,
          last_name: userData.lastName,
          email: userData.email,
          phone: userData.phone,
          roles: userData.roles,
        };

        const response = await httpClient.put(
          `settings/user-management/users/${userData.id}`,
          data
        );

        refetchUsers();
        toast.success("Changes saved!");
        isSuccess = true;
      } catch (error) {
        if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else if (error.response?.data?.errors) {
          Object.values(error.response.data.errors).forEach((errArr) => {
            errArr.forEach((msg) => toast.error(msg));
          });
        } else {
          toast.error("Your changes couldn’t be saved");
        }
      } finally {
        store.commit("preloader/preloader", false);
      }

      return isSuccess;
    },
    async inviteUser({}, userData) {
      let isSuccess = false;
      store.commit("preloader/preloader", true);
      try {
        const data = {
          first_name: userData.firstName,
          last_name: userData.lastName,
          email: userData.email,
          phone: userData.phone,
          roles: userData.roles,
        };

        const response = await httpClient.post(
          `settings/user-management/users`,
          data
        );
        refetchUsers();

        toast.success(`User invite sent!`);

        isSuccess = true;
      } catch (error) {
        toast.error(`Your changes couldn’t be saved`);
      } finally {
        store.commit("preloader/preloader", false);
      }
      return isSuccess;
    },
    async removeActiveUser({}, user) {
      store.commit("preloader/preloader", true);
      try {
        const response = await httpClient.post(
          `settings/user-management/users/${user.id}/delete`
        );
        refetchUsers();
        let message = "User deleted";

        toast.success(`${response?.data?.message ?? message}`);
      } catch (error) {
        toast.error(`Your changes couldn’t be saved`);
      } finally {
        store.commit("preloader/preloader", false);
      }
    },
    async resendInvitation({}, user) {
      store.commit("preloader/preloader", true);
      try {
        const response = await httpClient.post(
          `settings/user-management/users/${user.id}/resend-invitation`
        );

        toast.success(`${response?.data?.message}`);

        refetchUsers();
      } catch (error) {
        if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else if (error.response?.data?.errors) {
          Object.values(error.response.data.errors).forEach((errArr) => {
            errArr.forEach((msg) => toast.error(msg));
          });
        } else {
          toast.error("Failed to resend invitation");
        }
      } finally {
        store.commit("preloader/preloader", false);
      }
    },
  },
};
